import { useState } from 'react';
import { LinearProgress, CircularProgress, Typography, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { usePlans } from '../hooks/useAdmin';
import { useUpgradePlan, useUsage } from '../hooks/useCommon';
import { useAuth } from '../hooks/useAuth';
import { PlanCard } from './plan/PlanCard';
import { LoadingSpinner } from '@/components/ui/loading-spinner';
import { ErrorDisplay } from '@/components/ui/error-display';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

interface Plan {
  id: number;
  name: string;
  description: string;
  price: number;
  features: string;
  is_active: boolean;
}

interface PlanFeatures {
  monthly_limit: number;
  daily_limit: number;
  business_profiles_limit: number;
  platforms: string[];
  unlimited?: boolean;
  priority_support?: boolean;
}

const PlanManagement: React.FC = () => {
  const { user } = useAuth();
  const [selectedPlan, setSelectedPlan] = useState<number | null>(null);
  const [upgradeDialogOpen, setUpgradeDialogOpen] = useState(false);

  const { data: plans, isLoading: plansLoading, error: plansError } = usePlans();
  const { data: usage } = useUsage();
  
  // Use user data directly instead of separate API call
  const currentPlan = user?.plan_id ? { id: user.plan_id, name: 'Current Plan' } : null;
  const upgradePlan = useUpgradePlan();

  const parseFeatures = (featuresJson: string | string[]): PlanFeatures => {
    try {
      if (Array.isArray(featuresJson)) {
        return JSON.parse(featuresJson.join(''));
      }
      return JSON.parse(featuresJson);
    } catch {
      return {
        monthly_limit: 5,
        daily_limit: 5,
        business_profiles_limit: 1,
        platforms: ['email', 'blog']
      };
    }
  };

  const handleUpgrade = async () => {
    if (!selectedPlan) return;
    
    try {
      await upgradePlan.mutateAsync(selectedPlan.toString());
      setUpgradeDialogOpen(false);
      setSelectedPlan(null);
    } catch (error) {
      // console.error('Upgrade failed:', error);
    }
  };

  const currentPlanFeatures = currentPlan ? parseFeatures(currentPlan.features) : null;

  if (plansLoading || currentPlanLoading) {
    return <LoadingSpinner size="lg" />;
  }

  if (plansError) {
    return (
      <ErrorDisplay
        error={plansError}
        title="Failed to load plans"
        message="Please try again later"
        onRetry={() => window.location.reload()}
      />
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold mb-2">Choose Your Plan</h1>
        <p className="text-muted-foreground">Select the plan that best fits your business needs</p>
      </div>

      {currentPlan && usage && currentPlanFeatures && (
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Current Usage - {currentPlan.name} Plan</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <div className="flex justify-between mb-2">
                <span className="text-sm text-muted-foreground">Content Generations This Month</span>
                <span className="text-sm font-medium">
                  {usage.generations_this_month || 0} / {currentPlanFeatures.monthly_limit === -1 ? '∞' : currentPlanFeatures.monthly_limit}
                </span>
              </div>
              <LinearProgress 
                variant="determinate" 
                value={currentPlanFeatures.monthly_limit === -1 ? 0 : ((usage.generations_this_month || 0) / currentPlanFeatures.monthly_limit) * 100}
                sx={{ height: 8, borderRadius: 4 }}
              />
            </div>
            
            <div>
              <div className="flex justify-between mb-2">
                <span className="text-sm text-muted-foreground">Business Profiles</span>
                <span className="text-sm font-medium">
                  {usage.business_profiles || 0} / {currentPlanFeatures.business_profiles_limit === -1 ? '∞' : currentPlanFeatures.business_profiles_limit}
                </span>
              </div>
              <LinearProgress 
                variant="determinate" 
                value={currentPlanFeatures.business_profiles_limit === -1 ? 0 : ((usage.business_profiles || 0) / currentPlanFeatures.business_profiles_limit) * 100}
                sx={{ height: 8, borderRadius: 4 }}
              />
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {plans?.sort((a: Plan, b: Plan) => {
          // Define the desired order
          const order = ['Free', 'Starter', 'Pro', 'Agency'];
          const aIndex = order.indexOf(a.name);
          const bIndex = order.indexOf(b.name);
          return aIndex - bIndex;
        }).map((plan: Plan) => {
          const features = parseFeatures(plan.features);
          const isCurrentPlan = currentPlan?.id === plan.id;
          const isPopular = plan.name.toLowerCase() === 'starter';

          return (
            <PlanCard
              key={plan.id}
              plan={plan}
              features={features}
              isCurrentPlan={isCurrentPlan}
              isPopular={isPopular}
              onUpgrade={(planId) => {
                setSelectedPlan(planId);
                setUpgradeDialogOpen(true);
              }}
              isUpgrading={upgradePlan.isPending}
            />
          );
        })}
      </div>

      <Dialog 
        open={upgradeDialogOpen} 
        onClose={() => setUpgradeDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Confirm Plan Upgrade
        </DialogTitle>
        <DialogContent>
          <Typography className="mb-2">
            Are you sure you want to upgrade to the {plans?.find((p: any) => p.id === selectedPlan)?.name} plan?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            This will change your monthly billing and plan features immediately.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="outline" onClick={() => setUpgradeDialogOpen(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleUpgrade}
            disabled={upgradePlan.isPending}
          >
            {upgradePlan.isPending ? <CircularProgress size={20} /> : 'Confirm Upgrade'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default PlanManagement;
